import React, { useState } from "react";

import { action, makeAutoObservable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { joinClasses, changeView, useClasses } from "../globals";
import { signIn, signOut, getContext } from "../simgate";

import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

// Icons
import AutorenewIcon from "@material-ui/icons/Autorenew";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { Facebook, GTranslate, Twitter } from "@material-ui/icons";

class LoginStore {
  processing = false;

  email = "";
  emailError = "";

  password = "";
  passwordError = "";
  passwordVisible = false;

  serverError = "";

  get showEmailPassword() {
    // TODO: check based on enterprise/project
    return true;
  }

  get SSOOptions() {
    // TODO: check based on enterprise/project
    return false;
  }

  get showSSO() {
    return this.SSOOptions.length > 0;
  }

  constructor(app, email = "") {
    this.email = email || "";
    makeAutoObservable(this);
    this.app = app;
  }

  async emailPasswordLogin() {
    let cont = true;
    const { email, password } = this;

    runInAction(() => {
      this.processing = true;
      this.emailError = "";
      this.passwordError = "";
      this.serverError = "";

      if (!email) {
        this.emailError = "Email Address is required";
      }
      if (!password) {
        this.passwordError = "Password is required";
      }

      cont = Boolean(email) && Boolean(password);
      if (!cont) {
        this.processing = false;
      }
    });

    if (!cont) {
      return;
    }

    try {
      await signOut();
      const [result] = await signIn({
        username: this.email,
        password: this.password
      });

      if (result.error) {
        runInAction(() => {
          this.processing = false;
          console.log(result.message);
          this.serverError =
            "That email and password combination was not found. Please check your email and password and try again or click Reset password below.";
        });
      } else {
        console.log("Successfully logged in");
        const [context] = await getContext();
        runInAction(() => {
          this.app.context = context.context;
        });
      }
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.processing = false;
        this.serverError =
          "An unknown error occurred signing you in. Please wait 1 minute and try again.";
      });
    }
  }

  async ssoLogin() {}
}

const EmailField = observer(({ classes, store }) => {
  const { processing, email, emailError } = store;

  return (
    <Box className={classes._Margin}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        onFocus={action(() => {
          store.serverError = false;
          store.emailError = false;
        })}
        label="Email"
        value={email}
        error={Boolean(emailError)}
        disabled={processing}
        onChange={action(event => (store.email = event.target.value))}
        helperText={emailError}
        onKeyPress={e => {
          if (e.charCode === 13) {
            store.emailPasswordLogin();
          }
        }}
      />
    </Box>
  );
});

const PasswordField = observer(({ classes, store }) => {
  const { processing, password, passwordError, passwordVisible } = store;

  return (
    <Box className={classes._Margin}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        onFocus={action(() => {
          store.serverError = false;
          store.passwordError = false;
        })}
        type={passwordVisible ? "text" : "password"}
        label="Password"
        value={password}
        disabled={processing}
        helperText={passwordError}
        error={Boolean(passwordError)}
        onChange={action(event => (store.password = event.target.value))}
        onKeyPress={e => {
          if (e.charCode === 13) {
            store.emailPasswordLogin();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={action(
                  () => (store.passwordVisible = !store.passwordVisible)
                )}
                size="small"
                edge="end"
                tabIndex="-1">
                {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </Box>
  );
});

const LoginError = observer(({ classes, store }) => {
  const { serverError } = store;

  console.log(serverError);

  return (
    <Collapse in={Boolean(serverError)}>
      <Box mb={2}>
        <Alert severity="error">
          <Typography>{serverError}</Typography>
        </Alert>
      </Box>
    </Collapse>
  );
});

const LoginButton = observer(({ classes, store }) => {
  const { processing } = store;

  return (
    <Box
      className={joinClasses(
        classes._Flex,
        classes._FlexDirectionColumn,
        classes._Padding
      )}>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        startIcon={
          processing ? (
            <AutorenewIcon className={classes.processingIcon} />
          ) : null
        }
        onClick={() => store.emailPasswordLogin()}
        disabled={Boolean(processing)}>
        Sign in
      </Button>
    </Box>
  );
});

const ResetPassword = observer(({ classes, store }) => {
  const { processing, email } = store;

  return (
    <Grid item xs={6}>
      <Button
        fullWidth
        onClick={() => changeView("PasswordRecovery", { email })}
        disabled={processing}>
        Reset password
      </Button>
    </Grid>
  );
});

const SignUp = observer(({ classes, store }) => {
  const { processing, email } = store;

  return (
    <Grid item xs={6}>
      <Button
        fullWidth
        onClick={() => changeView("SignUp", { email })}
        disabled={processing}>
        Sign up
      </Button>
    </Grid>
  );
});

const SSO = observer(({ classes, store }) => {
  const { processing, email, SSOOptions, showSSO } = store;

  if (!showSSO) {
    return false;
  }

  return (
    <React.Fragment>
      <Box
        className={joinClasses(
          classes._Padding,
          classes._TextAlignCenter,
          classes._Flex,
          classes._AlignItemsCenter
        )}>
        <Box className={classes._FlexGrow1}>
          <Divider />
        </Box>
        <Box
          className={joinClasses(
            classes._FlexGrow0,
            classes._PaddingHorizontal
          )}>
          <Typography color="primary">Or sign in with</Typography>
        </Box>
        <Box className={classes._FlexGrow1}>
          <Divider />
        </Box>
      </Box>

      <Box className={classes._Margin}>
        <ButtonGroup
          variant="outlined"
          color="primary"
          orientation="vertical"
          fullWidth>
          {[
            {
              title: "Google",
              icon: <GTranslate />
            },
            {
              title: "Facebook",
              icon: <Facebook />
            },
            {
              title: "Twitter",
              icon: <Twitter />
            }
          ].map((item, index) => (
            <Button key={index} startIcon={item.icon}>
              {item.title}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
    </React.Fragment>
  );
});

export const EmailPasswordLogin = observer(({ app, email }) => {
  const classes = useClasses();
  const [store] = useState(() => new LoginStore(app, email));

  return (
    <React.Fragment>
      <Box
        className={joinClasses(
          classes._Flex,
          classes._FlexDirectionColumn,
          classes._Padding
        )}>
        <EmailField classes={classes} store={store} />
        <PasswordField classes={classes} store={store} />
        <LoginError classes={classes} store={store} />
        <LoginButton classes={classes} store={store} />
        <SSO classes={classes} store={store} />
      </Box>
      <Divider />
      <Box className={joinClasses(classes._PaddingDouble, classes._Flex)}>
        <Grid container spacing={2} className={classes._AlignItemsCenter}>
          <ResetPassword classes={classes} store={store} />
          <SignUp classes={classes} store={store} />
        </Grid>
      </Box>
    </React.Fragment>
  );
});

EmailPasswordLogin.title = "Sign In";
